import { Link } from '@mui/material';
import { GridRowModel, GridValidRowModel } from '@mui/x-data-grid';
import { GridRowProps } from '@mui/x-data-grid-pro';
import { convertsCentsToCurrency } from '@npi/buddy-utils';

import { routes } from '@redwoodjs/router';

import { NPIDataGrid, NPIDataGridColDef } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import CommissionType from 'src/features/finance/commissionStatement/constants/commissionType';
import { InvoiceItem } from 'types/graphql';

interface InvoiceItemsProps {
    lineItems: InvoiceItem[];
    usersCurrency: string;
}

export const getCommissionTypeFromDescription = (description: string): string => {
    if (!description) {
        return CommissionType.TRANSACTIONAL.toLowerCase();
    }

    switch (description.toUpperCase()) {
        case CommissionType.TERMINAL: {
            return CommissionType.TERMINAL.toLowerCase();
        }
        case CommissionType.ONLINE: {
            return CommissionType.ONLINE.toLowerCase();
        }
        default: {
            return CommissionType.TRANSACTIONAL.toLowerCase();
        }
    }
};

const GetHrefRoute = (row: GridRowModel): string => {
    return routes?.viewCommissionStatement({
        id: row.commissionStatementId.id,
        commissionType: getCommissionTypeFromDescription(row.description),
    });
};

const VIEW_SELF_BILLING_INVOICE_COLUMN_HEADERS: NPIDataGridColDef[] = [
    createColumnDef({
        field: 'descriptionId',
        headerName: 'Description',
        renderCell: (params) => {
            return (
                <div>
                    {params.row.description} (
                    <Link
                        sx={{ color: 'primary.light' }}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={GetHrefRoute(params.row)}
                    >
                        Statement ID {params.row.commissionStatementId.commissionStatementId}
                    </Link>
                    )
                </div>
            );
        },
    }),
    createColumnDef({
        field: 'quantity',
        headerName: 'Quantity',
    }),
    createColumnDef({
        field: 'netAmount',
        headerName: 'Net Amount',
    }),
    createColumnDef({
        field: 'totalLineVat',
        headerName: 'Total Line Vat',
    }),
    createColumnDef({
        field: 'lineTotal',
        headerName: 'Line Total',
    }),
];

const formatRowData = (invoiceItemsProps: InvoiceItemsProps): GridRowProps[] => {
    return (
        invoiceItemsProps.lineItems?.map((item: InvoiceItem) => {
            return {
                ...item,
                netAmount: `${invoiceItemsProps.usersCurrency}${convertsCentsToCurrency(item.netAmount)}`,
                totalLineVat: `${invoiceItemsProps.usersCurrency}${convertsCentsToCurrency(item.totalVat)}`,
                lineTotal: `${invoiceItemsProps.usersCurrency}${convertsCentsToCurrency(item.lineTotal)}`,
            };
        }) ?? []
    );
};

const InvoiceItems = (invoiceItemsProps: InvoiceItemsProps) => {
    const MAX_HEIGHT = '400';

    return (
        <NPIDataGrid
            key="invoice-items-table_x"
            dataTestId="invoice-items-table"
            rows={formatRowData(invoiceItemsProps)}
            columns={VIEW_SELF_BILLING_INVOICE_COLUMN_HEADERS}
            getRowId={(row: GridValidRowModel) => {
                return row.commissionStatementId.commissionStatementId;
            }}
            title="Invoice Items"
            maxHeight={MAX_HEIGHT}
            filtering={{ isEnabled: true }}
        />
    );
};

export default InvoiceItems;
